/**
 * Hedita Enterprise - usePermissions Hook
 * Nuovo schema: team_members con permissions JSONB
 */

import { useAuth } from '@/lib/useAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient';
import { DEFAULT_PERMISSIONS, LABELS_PERMISSIONS, type Permissions, type RuoloTeam } from './types';

const QUERY_KEY = 'team-member-permissions';

interface PermissionCheck extends Permissions {
  isAdmin: boolean;
  ruolo: RuoloTeam | null;
  teamMemberId: string | null;
  adminId: string | null;
}

export function usePermissions() {
  const { currentUser } = useAuth();
  
  const { data: permissionData, isLoading } = useQuery<PermissionCheck | null>({
    queryKey: [QUERY_KEY, currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return null;
      
      // 1. Verifica se è un Admin (proprietario)
      const { data: admin } = await supabase
        .from('admins')
        .select('id')
        .eq('auth_user_id', currentUser.id)
        .maybeSingle();
      
      if (admin) {
        // Admin ha tutti i permessi
        return {
          isAdmin: true,
          ruolo: 'direttore' as RuoloTeam,
          teamMemberId: null,
          adminId: admin.id,
          ...DEFAULT_PERMISSIONS.direttore,
          gestisce_team: true,
          gestisce_impostazioni: true,
        };
      }
      
      // 2. Altrimenti è un Team Member - carica i suoi permessi
      const { data: teamMember } = await supabase
        .from('team_members')
        .select('id, admin_id, ruolo, permissions, attivo')
        .eq('auth_user_id', currentUser.id)
        .eq('attivo', true)
        .maybeSingle();
      
      if (!teamMember) {
        return null;
      }
      
      // 3. Merge permessi default con permessi custom del DB
      const ruolo = (teamMember.ruolo || 'osservatore') as RuoloTeam;
      const defaultPerms = DEFAULT_PERMISSIONS[ruolo] || DEFAULT_PERMISSIONS.osservatore;
      const customPerms = teamMember.permissions || {};
      
      return {
        isAdmin: false,
        ruolo: ruolo,
        teamMemberId: teamMember.id,
        adminId: teamMember.admin_id,
        ...defaultPerms,
        ...customPerms,
      };
    },
    enabled: !!currentUser?.id,
  });
  
  const can = (action: keyof Permissions): boolean => {
    if (permissionData?.isAdmin) return true;
    return permissionData?.[action] ?? false;
  };
  
  const canSee = (section: string): boolean => {
    if (permissionData?.isAdmin) return true;
    
    const sectionMap: Record<string, keyof Permissions> = {
      'finanze': 'vede_finanze',
      'messaggi': 'vede_messaggi',
      'notifiche': 'vede_notifiche',
      'prenotazioni': 'gestisce_prenotazioni',
      'pulizie': 'gestisce_pulizie',
      'manutenzione': 'gestisce_manutenzione',
      'ospiti': 'gestisce_ospiti',
      'strutture': 'gestisce_strutture',
      'team': 'gestisce_team',
      'impostazioni': 'gestisce_impostazioni',
    };
    
    const permKey = sectionMap[section];
    if (!permKey) return false;
    
    return permissionData?.[permKey] ?? false;
  };
  
  // Verifica se è ruolo host (per Guest App)
  const isHost = (): boolean => {
    return permissionData?.ruolo === 'host' && !permissionData?.isAdmin;
  };
  
  // Ottieni l'admin_id per le query
  const getAdminId = (): string | null => {
    return permissionData?.adminId ?? null;
  };
  
  return {
    permissions: permissionData,
    isLoading,
    isAdmin: permissionData?.isAdmin ?? false,
    ruolo: permissionData?.ruolo ?? null,
    teamMemberId: permissionData?.teamMemberId ?? null,
    adminId: permissionData?.adminId ?? null,
    can,
    canSee,
    isHost,
    getAdminId,
  };
}

export default usePermissions;
